import { Fragment } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@shared/auth/AuthContext';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { Footer } from '@shared/ui';
import './admin.css';

// Nav grouped by the admin's mental model: accounts · catalog · growth/ops.
// (The TV/radio ingest group was retired on 2026-10-27 with those channels.)
const NAV_GROUPS: { to: string; key: string; dv: string }[][] = [
  [{ to: 'users', key: 'nav.accounts', dv: 'Аккаунты' }],
  [{ to: 'catalog', key: 'nav.catalog', dv: 'Каталог' }, { to: 'brands', key: 'nav.brands', dv: 'Бренды' }, { to: 'normalization', key: 'nav.normalization', dv: 'Нормализация' }],
  [{ to: 'marketing', key: 'nav.marketing', dv: 'Маркетинг' }, { to: 'health', key: 'nav.ops', dv: 'Ops' }],
];

export default function AdminLayout() {
  const { t } = useTranslation(['admin', 'common']);
  const { user, signOut } = useAuth();

  // New (unhandled) support tickets → a badge on the Ops nav so they don't sit unseen.
  const { data: newFeedback } = useSupabaseQuery<number>(
    ['feedback-new-count'],
    async () => {
      const res = await supabase.from('feedback').select('id', { count: 'exact', head: true }).or('status.eq.new,status.is.null');
      return { data: res.count ?? 0, error: res.error };
    },
    { refetchInterval: 120_000 },
  );

  return (
    <div className="admin-root">
      <header className="admin-head">
        <h1 className="brand">Ada<span>Mar</span> · {t('admin:nav.title')}</h1>
        <nav className="admin-nav">
          {NAV_GROUPS.map((group, gi) => (
            <Fragment key={gi}>
              {gi > 0 && <span aria-hidden style={{ width: 1, height: 16, alignSelf: 'center', background: 'rgba(255,255,255,0.14)', margin: '0 4px' }} />}
              {group.map((item) => (
                <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? 'active' : '')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {t(`admin:${item.key}`, { defaultValue: item.dv })}
                  {item.to === 'health' && (newFeedback ?? 0) > 0 && (
                    <span title={t('admin:nav.newFeedback', { defaultValue: 'Новые обращения' }) as string}
                      style={{ fontSize: 11, fontWeight: 700, lineHeight: 1, padding: '2px 6px', borderRadius: 999, color: '#f1c40f', background: 'rgba(241,196,15,0.16)', border: '1px solid rgba(241,196,15,0.45)' }}>
                      {newFeedback}
                    </span>
                  )}
                </NavLink>
              ))}
            </Fragment>
          ))}
        </nav>
        <LanguageSwitcher />
        <span className="email">{user?.email}</span>
        <button className="signout" onClick={signOut}>
          {t('common:nav.signOut')}
        </button>
      </header>
      <main className="admin-main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
